import { useState, useCallback, useEffect } from 'react';
import {
	TrashIcon,
	ChevronDownIcon,
	ChevronUpIcon,
} from '@heroicons/react/outline';
import { getProjectData } from '@appmaker/core/store/project';
import { SketchPicker } from 'react-color';
import Preview from './Preview';
import { useCurrentProjectExtensionInstalls, useUpdateExtensionInstall } from '../../../hooks/useAppmakerGql';

const ColorPicker = ({ color, onChange, label }) => {
	const [showPicker, setShowPicker] = useState(false);

	const handleColorChange = (colorObj) => {
		console.log('Color changed:', colorObj);
		onChange(colorObj);
	};

	return (
		<div>
			<label className="block text-sm font-medium text-gray-700 mb-2">
				{label}
			</label>
			<div className="relative">
				<div className="flex flex-row items-center gap-2 border border-gray-300 rounded-md py-2 px-6">
					<div
						className="h-6 w-6 rounded-md border border-gray-300 cursor-pointer"
						style={{ backgroundColor: color }}
						onClick={() => setShowPicker(!showPicker)}
					/>
					<div className="flex flex-col">
						<span className="text-sm">{color}</span>
					</div>
				</div>
				{showPicker && (
					<div className="absolute z-10 mt-2">
						<div
							className="fixed inset-0"
							onClick={() => setShowPicker(false)}
						/>
						<SketchPicker 
							color={color} 
							onChange={handleColorChange}
							presetColors={[
								'#2e17a5', '#7568b6', '#f0eef8', '#2c0fbe', '#0a0a0a',
								'#8c7cde', '#42338b', '#f0eef8', '#2c2a38', '#ebeaf1',
								'#3f2e90', '#f4f3f8', '#5644b0', '#FFFFFF', '#000000'
							]}
						/>
					</div>
				)}
			</div>
		</div>
	);
};

const InstallBanner = () => {
	const { projectData } = getProjectData();
	const { icon: projectIcon } = projectData;

	// Fetch API data
	const { data: extensionInstalls, isLoading, error } = useCurrentProjectExtensionInstalls({
		extension: {
			handle: {
				equals: 'plobal'
			}
		}
	});


	// Update mutation
	const updateMutation = useUpdateExtensionInstall({
		onSuccess: (data) => {
			console.log('Install Banner updated successfully:', data);
		},
		onError: (error) => {
			console.error('Failed to update Install Banner:', error);
		}
	});

	// Extract app_install_banner data from API
	const apiData = extensionInstalls?.[0]?.settings?.app_install_banner || null;
	const extensionInstallId = extensionInstalls?.[0]?.id;

	const getInitialFormData = () => {
		if (apiData) {
			return {
				icon: apiData.app_icon?.url || projectIcon || '',
				title: apiData.banner_title || 'Download Our App!',
				description: apiData.banner_description || 'Save 10% off your first order!',
				titleColor: {
					text: apiData.title_color?.text_color || '#2e17a5',
					description: apiData.title_color?.description_color || '#7568b6',
				},
				bgColor: apiData.title_color?.background_color || '#f0eef8',
				button: {
					text: apiData.button?.button_text || 'Download now',
					link: apiData.button?.button_link || '',
					textColor: apiData.button?.button_text_color || '#2c0fbe',
					backgroundColor: apiData.button?.button_color || '#0a0a0a',
				},
				advanced: {
					ipad: apiData.advanced_settings?.show_on_ipad_and_tablet || false,
					hideAfterClose: parseInt(apiData.advanced_settings?.hide_banner_after_discard_count) || 3,
					hideAfterDays: parseInt(apiData.advanced_settings?.hide_banner_after_discard_days) || 7,
					hideAfterInstall: parseInt(apiData.advanced_settings?.hide_banner_after_install_days) || 7,
				},
			};
		} else {
			// Fallback values when no API data
			return {
				icon: projectIcon || '',
				title: 'Download Our App!',
				description: 'Save 10% off your first order!',
				titleColor: {
					text: '#2e17a5',
					description: '#7568b6',
				},
				bgColor: '#f0eef8',
				button: {
					text: 'Download now',
					link: '',
					textColor: '#2c0fbe',
					backgroundColor: '#0a0a0a',
				},
				advanced: {
					ipad: false,
					hideAfterClose: 3,
					hideAfterDays: 7,
					hideAfterInstall: 7,
				},
			};
		}
	};

	const [formData, setFormData] = useState(getInitialFormData());

	// Update form data when API data loads
	useEffect(() => {
		if (!isLoading && extensionInstalls) {
			setFormData(getInitialFormData());
		}
	}, [extensionInstalls, isLoading]);

	const [isDragging, setIsDragging] = useState(false);
	const [showAdvanced, setShowAdvanced] = useState(false);

	const handleChange = (e) => {
		const { name, value, files, type, checked } = e.target;
		console.log('Form field changed:', { name, value, type, checked });
		
		if (files && files[0]) {
			setFormData((prev) => ({
				...prev,
				[name]: files[0],
			}));
		} else if (name.includes('.')) {
			const [parent, child] = name.split('.');
			setFormData((prev) => {
				const newData = {
					...prev,
					[parent]: {
						...prev[parent],
						[child]: type === 'checkbox' ? checked : value,
					},
				};
				console.log(`Updated ${parent}.${child}:`, newData[parent][child]);
				return newData;
			});
		} else {
			setFormData((prev) => {
				const newData = {
					...prev,
					[name]: type === 'checkbox' ? checked : value,
				};
				console.log(`Updated ${name}:`, newData[name]);
				return newData;
			});
		}
	};

	const handleColorChange = (color, type) => {
		console.log('handleColorChange called:', { color, type });
		switch (type) {
			case 'bgColor':
				setFormData((prev) => {
					const newData = { ...prev, bgColor: color.hex };
					console.log('Updated bgColor:', newData.bgColor);
					return newData;
				});
				break;
			case 'textColor':
				setFormData((prev) => {
					const newData = {
						...prev,
						titleColor: {
							...prev.titleColor,
							text: color.hex,
						},
					};
					console.log('Updated textColor:', newData.titleColor.text);
					return newData;
				});
				break;
			case 'descriptionColor':
				setFormData((prev) => {
					const newData = {
						...prev,
						titleColor: {
							...prev.titleColor,
							description: color.hex,
						},
					};
					console.log('Updated descriptionColor:', newData.titleColor.description);
					return newData;
				});
				break;
			case 'buttonColor':
				setFormData((prev) => {
					const newData = {
						...prev,
						button: {
							...prev.button,
							backgroundColor: color.hex,
						},
					};
					console.log('Updated buttonColor:', newData.button.backgroundColor);
					return newData;
				});
				break;
			case 'buttonTextColor':
				setFormData((prev) => {
					const newData = {
						...prev,
						button: {
							...prev.button,
							textColor: color.hex,
						},
					};
					console.log('Updated buttonTextColor:', newData.button.textColor);
					return newData;
				});
				break;
			default:
				console.warn('Unknown color type:', type);
				break;
		}
	};

	const handleDragOver = useCallback((e) => {
		e.preventDefault();
		setIsDragging(true);
	}, []);

	const handleDragLeave = useCallback((e) => {
		e.preventDefault();
		setIsDragging(false);
	}, []);

	const handleDrop = useCallback((e) => {
		e.preventDefault();
		setIsDragging(false);

		const file = e.dataTransfer.files[0];
		if (file && file.type.startsWith('image/')) {
			setFormData((prev) => ({
				...prev,
				icon: file,
			}));
		}
	}, []);

	const removeIcon = () => {
		setFormData((prev) => ({
			...prev,
			icon: '',
		}));
	};

	const handleSubmit = (e) => {
		e.preventDefault();
		console.log('Form submitted with data:', formData);

		// Transform form data back to API structure
		const apiFormattedData = {
			app_install_banner: {
				app_icon: {
					url: formData.icon,
					// If it's a new file upload, we'd handle this differently in real implementation
					id: apiData?.app_icon?.id || null,
					meta: apiData?.app_icon?.meta || null,
					title: apiData?.app_icon?.title || ""
				},
				banner_title: formData.title,
				banner_description: formData.description,
				title_color: {
					text_color: formData.titleColor.text,
					description_color: formData.titleColor.description,
					background_color: formData.bgColor,
				},
				button: {
					button_text: formData.button.text,
					button_link: formData.button.link,
					button_text_color: formData.button.textColor,
					button_color: formData.button.backgroundColor,
				},
				advanced_settings: {
					show_on_ipad_and_tablet: formData.advanced.ipad,
					hide_banner_after_discard_count: formData.advanced.hideAfterClose.toString(),
					hide_banner_after_discard_days: formData.advanced.hideAfterDays.toString(),
					hide_banner_after_install_days: formData.advanced.hideAfterInstall.toString(),
				},
			}
		};

		console.log('API formatted data:', apiFormattedData);

		// Send mutation if we have an extension install ID
		if (extensionInstallId) {
			console.log('Updating with extension install ID:', extensionInstallId);
			// Merge with existing settings to preserve other data
			const currentSettings = extensionInstalls[0].settings || {};
			const updatedSettings = {
				...currentSettings,
				...apiFormattedData
			};
			
			console.log('Final settings to update:', updatedSettings);
			
			updateMutation.mutate({
				where: { id: extensionInstallId },
				data: { settings: updatedSettings }
			});
		} else {
			console.error('No extension install ID found. Cannot update settings.');
		}
	};

	const getIconUrl = () => {
		if (formData.icon instanceof File) {
			return URL.createObjectURL(formData.icon);
		}
		return formData.icon;
	};

	// Show loading state
	if (isLoading) {
		return (
			<div className="p-6">
				<div className="mb-6">
					<div className="animate-pulse">
						<div className="h-8 bg-gray-200 rounded w-1/3 mb-2"></div>
						<div className="h-4 bg-gray-200 rounded w-1/2"></div>
					</div>
				</div>

				<div className="flex gap-8">
					{/* Form Section Skeleton */}
					<div className="flex-1 space-y-6">
						{/* Icon Upload Skeleton */}
						<div>
							<div className="h-4 bg-gray-200 rounded w-20 mb-2"></div>
							<div className="border-2 border-dashed border-gray-300 rounded-lg p-8">
								<div className="flex justify-center">
									<div className="h-32 w-32 bg-gray-200 rounded-lg animate-pulse"></div>
								</div>
							</div>
						</div>

						{/* Title Input Skeleton */}
						<div>
							<div className="h-4 bg-gray-200 rounded w-24 mb-2"></div>
							<div className="h-10 bg-gray-200 rounded animate-pulse"></div>
						</div>

						{/* Description Input Skeleton */}
						<div>
							<div className="h-4 bg-gray-200 rounded w-28 mb-2"></div>
							<div className="h-20 bg-gray-200 rounded animate-pulse"></div>
						</div>

						{/* Color Picker Skeletons */}
						<div>
							<div className="h-4 bg-gray-200 rounded w-24 mb-2"></div>
							<div className="flex gap-4">
								<div className="flex-1">
									<div className="h-4 bg-gray-200 rounded w-20 mb-2"></div>
									<div className="h-12 bg-gray-200 rounded animate-pulse"></div>
								</div>
								<div className="flex-1">
									<div className="h-4 bg-gray-200 rounded w-28 mb-2"></div>
									<div className="h-12 bg-gray-200 rounded animate-pulse"></div>
								</div>
							</div>
						</div>

						{/* Background Color Skeleton */}
						<div>
							<div className="h-4 bg-gray-200 rounded w-32 mb-2"></div>
							<div className="h-12 bg-gray-200 rounded animate-pulse"></div>
						</div>

						{/* Button Section Skeleton */}
						<div>
							<div className="h-4 bg-gray-200 rounded w-16 mb-2"></div>
							<div className="space-y-4">
								<div>
									<div className="h-4 bg-gray-200 rounded w-12 mb-2"></div>
									<div className="h-10 bg-gray-200 rounded animate-pulse"></div>
								</div>
								<div>
									<div className="h-4 bg-gray-200 rounded w-16 mb-2"></div>
									<div className="h-10 bg-gray-200 rounded animate-pulse"></div>
								</div>
								<div className="flex gap-4">
									<div className="flex-1">
										<div className="h-4 bg-gray-200 rounded w-20 mb-2"></div>
										<div className="h-12 bg-gray-200 rounded animate-pulse"></div>
									</div>
									<div className="flex-1">
										<div className="h-4 bg-gray-200 rounded w-24 mb-2"></div>
										<div className="h-12 bg-gray-200 rounded animate-pulse"></div>
									</div>
								</div>
							</div>
						</div>

						{/* Advanced Settings Skeleton */}
						<div>
							<div className="h-4 bg-gray-200 rounded w-36 mb-2"></div>
							<div className="space-y-3">
								<div className="flex items-center gap-3">
									<div className="h-4 w-4 bg-gray-200 rounded animate-pulse"></div>
									<div className="h-4 bg-gray-200 rounded w-32"></div>
								</div>
								<div className="flex gap-4">
									<div className="flex-1">
										<div className="h-4 bg-gray-200 rounded w-24 mb-2"></div>
										<div className="h-10 bg-gray-200 rounded animate-pulse"></div>
									</div>
									<div className="flex-1">
										<div className="h-4 bg-gray-200 rounded w-20 mb-2"></div>
										<div className="h-10 bg-gray-200 rounded animate-pulse"></div>
									</div>
								</div>
								<div>
									<div className="h-4 bg-gray-200 rounded w-28 mb-2"></div>
									<div className="h-10 bg-gray-200 rounded animate-pulse"></div>
								</div>
							</div>
						</div>

						{/* Save Button Skeleton */}
						<div className="pt-4">
							<div className="h-10 bg-gray-200 rounded animate-pulse"></div>
						</div>
					</div>

					{/* Preview Section Skeleton */}
					<div className="w-80">
						<div className="h-4 bg-gray-200 rounded w-20 mb-4"></div>
						<div className="bg-gray-100 rounded-lg p-4 h-96">
							<div className="space-y-4">
								<div className="flex items-center gap-3">
									<div className="h-12 w-12 bg-gray-200 rounded-lg animate-pulse"></div>
									<div className="flex-1">
										<div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
										<div className="h-3 bg-gray-200 rounded w-1/2"></div>
									</div>
								</div>
								<div className="h-8 bg-gray-200 rounded animate-pulse"></div>
							</div>
						</div>
					</div>
				</div>
			</div>
		);
	}

	// Show error state
	if (error) {
		return (
			<div className="mb-6 mt-6">
				<div className="bg-red-50 border border-red-200 rounded-md p-4">
					<h2 className="text-lg font-medium text-red-800">Error Loading Data</h2>
					<p className="text-red-600">{error.message}</p>
				</div>
			</div>
		);
	}

	return (
		<>
			<div className="mb-6 mt-6">
				<div>
					<h2 className="text-2xl font-bold text-gray-900">Install Banner</h2>
					<p className="mt-1 text-sm text-gray-500">
						Create and customize your app install banner
					</p>
				</div>
			</div>

			<div className="flex gap-8">
				{/* Form Section */}
				<div className="flex-1 p-0">
					<form className="space-y-6" onSubmit={handleSubmit}>
						<div>
							<label className="block text-sm font-medium text-gray-700 mb-2">
								App Icon
							</label>
							<div
								className={`mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-lg ${isDragging
										? 'border-indigo-500 bg-indigo-50'
										: 'border-gray-300'
									}`}
								onDragOver={handleDragOver}
								onDragLeave={handleDragLeave}
								onDrop={handleDrop}
							>
								<div className="space-y-1 text-center">
									{formData.icon ? (
										<div className="relative">
											<img
												src={getIconUrl()}
												alt="App Icon"
												className="mx-auto h-32 w-32 object-contain rounded-lg"
											/>
											<button
												type="button"
												onClick={removeIcon}
												className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 focus:outline-none"
											>
												<TrashIcon className="h-4 w-4" />
											</button>
										</div>
									) : (
										<>
											<svg
												className="mx-auto h-12 w-12 text-gray-400"
												stroke="currentColor"
												fill="none"
												viewBox="0 0 48 48"
												aria-hidden="true"
											>
												<path
													d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
													strokeWidth={2}
													strokeLinecap="round"
													strokeLinejoin="round"
												/>
											</svg>
											<div className="flex text-sm text-gray-600">
												<label
													htmlFor="icon"
													className="relative cursor-pointer bg-white rounded-md font-medium text-indigo-600 hover:text-indigo-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-indigo-500"
												>
													<span>Upload a file</span>
													<input
														id="icon"
														name="icon"
														type="file"
														className="sr-only"
														accept="image/*"
														onChange={handleChange}
													/>
												</label>
												<p className="pl-1">or drag and drop</p>
											</div>
											<p className="text-xs text-gray-500">
												PNG, JPG, GIF up to 10MB
											</p>
										</>
									)}
								</div>
							</div>
						</div>

						<div>
							<label
								htmlFor="title"
								className="block text-sm font-medium text-gray-700"
							>
								Banner Title
							</label>
							<input
								type="text"
								name="title"
								id="title"
								value={formData.title}
								onChange={handleChange}
								className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
								placeholder="Enter banner title"
							/>
						</div>

						<div>
							<label
								htmlFor="description"
								className="block text-sm font-medium text-gray-700"
							>
								Description
							</label>
							<textarea
								id="description"
								name="description"
								value={formData.description}
								onChange={handleChange}
								rows={3}
								className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
								placeholder="Enter banner description"
							/>
						</div>

						<div>
							<label
								htmlFor="titleColor"
								className="block font-medium text-gray-700"
							>
								Title Color
							</label>
							<div className="flex flex-row gap-2 mt-2 items-center justify-between">
								<ColorPicker
									color={formData.titleColor.text}
									onChange={(color) => handleColorChange(color, 'textColor')}
									label="Text Color"
								/>

								<ColorPicker
									color={formData.titleColor.description}
									onChange={(color) =>
										handleColorChange(color, 'descriptionColor')
									}
									label="Description Color"
								/>
							</div>
						</div>

						<div>
							<label
								htmlFor="titleColor"
								className="block font-medium text-gray-700"
							>
								Background Color
							</label>
							<div className="flex flex-row gap-2 mt-2 items-center justify-between">
								<ColorPicker
									color={formData.bgColor}
									onChange={(color) => handleColorChange(color, 'bgColor')}
									label="Color"
								/>
							</div>
						</div>

						<span className="block font-medium text-gray-700">Button</span>

						<div>
							<label
								htmlFor="button.text"
								className="block text-sm font-medium text-gray-700"
							>
								Text
							</label>
							<input
								type="text"
								name="button.text"
								id="button.text"
								value={formData.button.text}
								onChange={handleChange}
								className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
								placeholder="Enter button text"
							/>
						</div>

						<div>
							<label
								htmlFor="titleColor"
								className="block font-medium text-gray-700"
							>
								Button Color
							</label>
							<div className="flex flex-row gap-2 mt-2 items-center justify-between">
								<ColorPicker
									color={formData.button.backgroundColor}
									onChange={(color) => handleColorChange(color, 'buttonColor')}
									label="Color"
								/>
								<ColorPicker
									color={formData.button.textColor}
									onChange={(color) =>
										handleColorChange(color, 'buttonTextColor')
									}
									label="Text Color"
								/>
							</div>
						</div>

						<div>
							<label
								htmlFor="button.link"
								className="block text-sm font-medium text-gray-700"
							>
								Button Link
							</label>
							<input
								type="text"
								name="button.link"
								id="button.link"
								value={formData.button.link}
								onChange={handleChange}
								className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
								placeholder="Enter button link"
							/>
						</div>

						{/* Advanced Settings */}
						<div className="border-t border-gray-200 pt-4">
							<button
								type="button"
								onClick={() => setShowAdvanced(!showAdvanced)}
								className="flex items-center justify-between w-full text-left"
							>
								<h3 className="text-lg font-medium text-blue-900">
									Advanced Settings
								</h3>
								{showAdvanced ? (
									<ChevronUpIcon className="h-5 w-5 text-gray-500" />
								) : (
									<ChevronDownIcon className="h-5 w-5 text-gray-500" />
								)}
							</button>

							{showAdvanced && (
								<div className="mt-4 space-y-4">
									<div className="flex items-center">
										<input
											type="checkbox"
											id="ipad"
											name="advanced.ipad"
											checked={formData.advanced.ipad}
											onChange={handleChange}
											className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
										/>
										<label
											htmlFor="ipad"
											className="ml-2 block text-sm text-gray-900"
										>
											Show on Ipad and Tablet
										</label>
									</div>

									<div>
										<label
											htmlFor="hideAfterClose"
											className="block text-sm font-medium text-gray-700"
										>
											Hide Install Banner after 'X' number of discards
										</label>
										<input
											type="number"
											name="advanced.hideAfterClose"
											id="hideAfterClose"
											value={formData.advanced.hideAfterClose}
											onChange={handleChange}
											className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
											min="0"
											max="10"
										/>
									</div>

									<div>
										<label
											htmlFor="hideAfterDays"
											className="block text-sm font-medium text-gray-700"
										>
											Hide Install Banner once you discard the banner for 'X'
											days
										</label>
										<input
											type="number"
											name="advanced.hideAfterDays"
											id="hideAfterDays"
											value={formData.advanced.hideAfterDays}
											onChange={handleChange}
											className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
											min="0"
											max="10"
										/>
									</div>

									<div>
										<label
											htmlFor="hideAfterInstall"
											className="block text-sm font-medium text-gray-700"
										>
											Hide Install Banner once you install the app for 'X' days
										</label>
										<input
											type="number"
											name="advanced.hideAfterInstall"
											id="hideAfterInstall"
											value={formData.advanced.hideAfterInstall}
											onChange={handleChange}
											className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
											min="0"
											max="10"
										/>
									</div>
								</div>
							)}
						</div>
					</form>
				</div>

				{/* Preview Section */}
				<div className="flex flex-col">
					<Preview formData={formData} getIconUrl={getIconUrl} />
					<div className="mt-4">
						<button
							type="button"
							onClick={handleSubmit}
							disabled={updateMutation.isLoading}
							className="w-full inline-flex justify-center items-center rounded-md border border-transparent bg-indigo-600 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
						>
							{updateMutation.isLoading ? (
								<>
									<svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
										<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
										<path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
									</svg>
									Saving...
								</>
							) : (
								'Save & Preview'
							)}
						</button>
					</div>
				</div>
			</div>
		</>
	);
};

export default InstallBanner;
